import React, { useEffect, useState } from 'react';
import { copyToClipboard, formatAgo, formatCountdown, formatUptime, useAdmin } from '../context';
import { DebugTarget, ServerStatus } from '../types';
import { Empty, Loading, ViewHeader } from '../ui';

// ---------------------------------------------------------------------------
// Status
// ---------------------------------------------------------------------------

const StatusViewInner: React.FC<{ status: ServerStatus | null; statusAt: number; knownCount: number }> = ({ status, statusAt, knownCount }) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, []);

  return (
    <div className="admin-panel__view">
      <ViewHeader title="Statut du serveur" subtitle={status ? `Mis à jour ${formatAgo(statusAt, now)}, actualisé toutes les 5 secondes.` : undefined} />
      {!status ? (
        <Loading text="Chargement du statut du serveur..." shape="stats" />
      ) : (
        <div className="admin-panel__stat-grid">
          <div className="admin-panel__stat">
            <span className="admin-panel__status-label">TPS</span>
            <span className="admin-panel__stat-value">{status.tps != null ? status.tps.toFixed(1) : '-'}</span>
            <span className="admin-panel__stat-note">Cadence de la boucle de jeu du serveur</span>
          </div>
          <div className="admin-panel__stat">
            <span className="admin-panel__status-label">Joueurs en ligne</span>
            <span className="admin-panel__stat-value">{status.onlinePlayers}</span>
            <span className="admin-panel__stat-note">{knownCount} personnages connus au total</span>
          </div>
          <div className="admin-panel__stat">
            <span className="admin-panel__status-label">Durée de fonctionnement</span>
            <span className="admin-panel__stat-value admin-panel__stat-value--text">{formatUptime(status.uptimeSec)}</span>
            <span className="admin-panel__stat-note">Depuis le dernier démarrage</span>
          </div>
          {status.restartInSec != null ? (
            <div className="admin-panel__stat">
              <span className="admin-panel__status-label">Redémarrage programmé</span>
              <span className="admin-panel__stat-value">{formatCountdown(status.restartInSec)}</span>
              <span className="admin-panel__stat-note">Avant le redémarrage du serveur</span>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Debug: what the admin is looking at
// ---------------------------------------------------------------------------

const DebugViewInner: React.FC<{ target: DebugTarget | null }> = ({ target }) => {
  const { toast } = useAdmin();

  const report = (t: DebugTarget): string =>
    [
      `Nom: ${t.name}`,
      `refId: ${t.refId}${t.refDesc ? ` (${t.refDesc})` : ''}`,
      `baseId: ${t.baseId}${t.baseDesc ? ` (${t.baseDesc})` : ''}`,
      `Cellule: ${t.cellName || '-'} (${t.cell})`,
      `Position: ${t.pos.join(', ')}`,
      `Distance: ${t.dist}u`,
    ].join('\n');

  return (
    <div className="admin-panel__view">
      <ViewHeader title="Debug" subtitle="Identité de l'objet ou du personnage que vous visez.">
        {target ? (
          <button
            type="button"
            className="admin-panel__button"
            onClick={() => {
              const ok = copyToClipboard(report(target));
              toast(ok ? 'Rapport copié.' : 'Copie impossible.', { kind: ok ? 'ok' : 'err' });
            }}
          >
            Copier le rapport
          </button>
        ) : null}
      </ViewHeader>
      {!target ? (
        <Empty title="Aucune cible" text="Visez quelque chose, ou regardez autour de vous, pour voir son identité. Le panel reste ouvert pendant ce temps." />
      ) : (
        <div className="admin-panel__card admin-panel__card--narrow">
          <div className="admin-panel__card-title">
            {target.name}
            {!target.live ? <span className="admin-panel__tag">Dernière position connue</span> : null}
          </div>
          <dl className="admin-panel__dl admin-panel__dl--wide">
            <dt>refId</dt>
            <dd>{target.refId}{target.refDesc ? ` (${target.refDesc})` : ''}</dd>
            <dt>baseId</dt>
            <dd>{target.baseId}{target.baseDesc ? ` (${target.baseDesc})` : ''}</dd>
            <dt>Cellule</dt>
            <dd>{target.cellName || '-'} ({target.cell})</dd>
            <dt>Position</dt>
            <dd>{target.pos.join(', ')}</dd>
            <dt>Distance</dt>
            <dd>{target.dist} u</dd>
          </dl>
        </div>
      )}
    </div>
  );
};

export const StatusView = React.memo(StatusViewInner);
export const DebugView = React.memo(DebugViewInner);
